import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/shared/state/authstore'
import { openChatEventStream } from '@/shared/realtime/chatEventStream'
import { chatKeys } from './keys'
import { useChatUiStore } from './chatUiStore'
import { useChatOutboxStore } from './chatOutboxStore'
import { queueDelivered, clearDeliveredQueue } from './deliveredQueue'
import { appendMessageToCache } from './mutations'
import { resyncThread, useConversations } from './queries'
import { useChatCapabilities } from './chatCapabilities'
import { PERMISSIONS_QUERY_KEY } from '@/entities/session/model/keys'
import type { ChatConversation, ChatConversationDetail } from '../api/types'

export function useChatStream(): void {
  const queryClient = useQueryClient()
  const accountId = useAuthStore((state) => state.accountId)
  const userId = useAuthStore((state) => state.userId)
  const { enabled: permitted } = useChatCapabilities()

  useConversations({ poll: true })

  const identityRef = useRef<string | null>(null)

  useEffect(() => {
    const identity = `${accountId}:${userId}`
    if (identityRef.current !== null && identityRef.current !== identity) {
      useChatUiStore.getState().reset()
      useChatOutboxStore.getState().clearAll()
    }
    identityRef.current = identity
  }, [accountId, userId])

  useEffect(() => {
    if (accountId === null || userId === null || !permitted) return

    const ui = useChatUiStore.getState()
    ui.setAccessEnded(false)

    const close = openChatEventStream(accountId, {
      onStatus: (status) => {
        useChatUiStore.getState().setStreamStatus(status)
        if (status === 'live') {
          void queryClient.invalidateQueries({ queryKey: chatKeys.conversations(accountId) })
          const visible = useChatUiStore.getState().visibleConversationId
          if (visible !== null) void resyncThread(queryClient, accountId, visible)
        }
      },
      onAccessEnded: () => {
        useChatUiStore.getState().setAccessEnded(true)
        queryClient.removeQueries({ queryKey: chatKeys.root })
        clearDeliveredQueue()
        void queryClient.invalidateQueries({ queryKey: PERMISSIONS_QUERY_KEY })
      },
      onFrame: (frame) => {
        const visible = useChatUiStore.getState().visibleConversationId

        if (frame.type === 'message.created') {
          if (frame.message.senderUserId !== userId) {
            useChatOutboxStore.getState().markSent(frame.conversationId, frame.message.clientRef)
          }
          appendMessageToCache(queryClient, accountId, frame.conversationId, frame.message)
          if (frame.message.kind === 'System') {
            void queryClient.invalidateQueries(
              { queryKey: chatKeys.conversation(accountId, frame.conversationId) },
              { cancelRefetch: false },
            )
          }
          if (frame.conversationId !== visible) {
            queueDelivered(frame.conversationId, frame.message.id)
          }
          void queryClient.invalidateQueries(
            { queryKey: chatKeys.conversations(accountId) },
            { cancelRefetch: false },
          )
          return
        }

        if (frame.type === 'message.deleted') {
          void queryClient.invalidateQueries({
            queryKey: chatKeys.messages(accountId, frame.conversationId),
          })
          return
        }

        if (frame.type === 'conversation.updated') {
          queryClient.setQueryData<ChatConversation[]>(chatKeys.conversations(accountId), (rows) =>
            rows?.map((row) =>
              row.id === frame.conversationId
                ? {
                    ...row,
                    lastMessageAt: frame.lastMessageAt,
                    lastMessagePreview: frame.lastMessagePreview,
                    lastSenderName: frame.lastSenderName,
                    lastMessageSenderUserId: null,
                  }
                : row,
            ),
          )
          return
        }

        if (frame.type === 'conversation.cursor') {
          const detailKey = chatKeys.conversation(accountId, frame.conversationId)
          const existingDetail = queryClient.getQueryData<ChatConversationDetail>(detailKey)
          const memberIndex = existingDetail
            ? existingDetail.members.findIndex((member) => frame.userId != null && member.userId === frame.userId)
            : -1

          if (memberIndex === -1) {
            void queryClient.invalidateQueries({ queryKey: detailKey })
            return
          }

          queryClient.setQueryData<ChatConversationDetail>(detailKey, (current) => {
            if (!current) return current
            return {
              ...current,
              members: current.members.map((member, index) =>
                index === memberIndex
                  ? {
                      ...member,
                      lastDeliveredMessageId: frame.lastDeliveredMessageId,
                      lastReadMessageId: frame.lastReadMessageId,
                    }
                  : member,
              ),
            }
          })
        }
      },
    })

    return () => {
      close()
      clearDeliveredQueue()
    }
  }, [accountId, userId, permitted, queryClient])
}
